// The canonical Pomela TopBar — one sticky header across every
// secondary screen. Promoted out of screens/Settings.tsx so the 8
// callers (Account, AdminDashboard, Copilot, Guide, MyApps,
// Settings, Upgrade) share a single source of truth for layout,
// typography, focus rings, and press feedback.
//
// Backward compat: screens/Settings.tsx re-exports `TopBar` from
// here, so every existing `import { TopBar } from './Settings'` keeps
// resolving. New code should import from this file directly.
import type { JSX, ComponentChildren } from 'preact';
import { ChevronLeft, Menu, type LucideIcon } from 'lucide-preact';
import { t } from '../lib/i18n';

interface TopBarProps {
  title: string;
  /** Back-button handler. The chevron is hidden on the root screen. */
  onClose: () => void;
  /** Optional trailing slot — actions like "Save" or "Edit". */
  actions?: ComponentChildren;
  /**
   * Optional contextual glyph rendered in a gradient chip to the left
   * of the title. Lifts every secondary screen out of "bare-text header"
   * territory without any per-screen bespoke chrome.
   */
  icon?: LucideIcon;
  /**
   * Optional category / role line above the title. Use for short
   * single words ("Build partner", "Account", "Concierge") — never a
   * sentence. Adds the editorial-magazine feel premium products earn.
   */
  eyebrow?: string;
}

/**
 * The canonical secondary-screen header.
 *
 *   ┌────────────────────────────────────────────────────────────────┐
 *   │ [mark]  [☰]  ←Back   Title                          [actions]  │
 *   └────────────────────────────────────────────────────────────────┘
 *      ↑ mobile only       ↑ always           ↑ always   ↑ optional
 *
 * Chrome:
 *   * sticky top:0 + blurred translucent bg (Apple TopBar pattern)
 *   * 1px hairline at the bottom in --border
 *   * z-index: 10 (sticky tier per styles.css scale)
 *   * Brand mark + hamburger hidden ≥768px (NavRail covers both)
 *   * Title gets --text-lg + --weight-semibold + tight letter-spacing
 *
 * Micro-interactions:
 *   * Buttons get `.btn-press` (scale 96% on press)
 *   * Brand mark gets `.lift-hover` (1 px lift on hover)
 *   * Focus rings inherited from global :focus-visible (4-px brand halo)
 */
export function TopBar({ title, onClose, actions, icon: Icon, eyebrow }: TopBarProps): JSX.Element {
  return (
    <div className="topbar">
      {/* Sidebar opener — mobile only. The mobile Pomela mascot was
          removed: the brand identity lives in the NavRail (desktop) and
          the Sidebar header (mobile), so a third mascot in the topbar
          read as clutter without adding navigation value. */}
      <button
        type="button"
        className="topbar-menu topbar-mobile-only btn-press"
        onClick={() => {
          navigator.vibrate?.(8);
          window.dispatchEvent(new CustomEvent('pomela:open-sidebar'));
        }}
        aria-label={t('common.openMenu')}
        title={t('common.menu')}
      >
        <Menu size={20} strokeWidth={2.25} />
      </button>
      {/* Back chevron — always present so users have one consistent
          way to peel off a secondary screen. */}
      <button
        type="button"
        className="topbar-back btn-press"
        onClick={onClose}
        aria-label={t('common.goBack')}
      >
        <ChevronLeft size={17} strokeWidth={2.25} />
        <span className="topbar-back__label">{t('common.back')}</span>
      </button>
      <div className="topbar-title">
        {Icon && (
          <span className="topbar-title__chip" aria-hidden="true">
            <Icon size={15} strokeWidth={2.25} />
          </span>
        )}
        <div className="topbar-title__lockup">
          {eyebrow && <span className="topbar-title__eyebrow">{eyebrow}</span>}
          <h1 className="topbar-title__h1">{title}</h1>
        </div>
      </div>
      {actions ? <div className="topbar-actions">{actions}</div> : null}
    </div>
  );
}
